/**
 * The Enrich step's before/after — "Option A · Split Ledger", the layout Baptiste picked
 * from the design canvas on 2026-09-01.
 *
 * Merchant baseline on the left, learned layer on the right, matched rows ALIGNED so the
 * before and the after never scroll apart. The glyph between them states the relationship,
 * and the relationship is computed deterministically (see lib/checks.merchantLedger) —
 * `+` is the honest measure of what enrichment actually added.
 */
import React from 'react';
import type {
  CockpitProduct, DictionaryEntry, SemanticGap,
} from '@/services/productCockpitService';
import { merchantLedger, feedOriginMatch, type LedgerRelation } from '../lib/checks';
import { LearnedAttrRow } from './AttrRow';
import { fmtInt } from '../lib/stages';

const GLYPH: Record<LedgerRelation, { g: string; cls: string; tip: string }> = {
  exact: { g: '=', cls: 'pmc-lg-eq', tip: 'value provably EQUAL to this merchant field (full-value equality)' },
  normalized: { g: '~', cls: 'pmc-lg-norm', tip: 'same field, different spelling — the AI normalised the merchant value' },
  derived: { g: '~', cls: 'pmc-lg-norm', tip: 'parsed or derived from this merchant spec (the learned value is contained in it)' },
  new: { g: '+', cls: 'pmc-lg-new', tip: 'NET NEW — nowhere in the merchant data; extracted from the description. The enrichment value-add.' },
};

interface SplitLedgerProps {
  product: CockpitProduct;
  dictionary: Record<string, DictionaryEntry> | null;
  /** run the value checks (Interpret step and beyond) */
  check: boolean;
  gaps: SemanticGap[];
}

export function SplitLedger({ product, dictionary, check, gaps }: SplitLedgerProps) {
  const { pairs, leftovers, counts } = merchantLedger(product);

  return (
    <>
      <div className="pmc-ledger">
        <div className="pmc-lg-head">
          what the merchant sent
          <span className="pmc-small pmc-mute pmc-lg-sub">feed + technical_specifications, verbatim</span>
        </div>
        <div />
        <div className="pmc-lg-head pmc-learned">
          learned by AI enrichment
          <span className="pmc-small pmc-mute pmc-lg-sub">
            {counts.exact} = · {counts.normalized + counts.derived} ~ · {counts.new} + net new
          </span>
        </div>

        {pairs.map((pr, i) => {
          const { g, cls, tip } = GLYPH[pr.rel];
          return (
            <React.Fragment key={`${pr.attr.key}-${i}`}>
              {pr.field
                ? (
                  <div className="pmc-lg-mcell">
                    <span className="pmc-lg-mkey">{pr.field.key}</span>
                    <span className="pmc-lg-mval">
                      {String(pr.field.value ?? '—').slice(0, 90)}
                    </span>
                  </div>
                )
                : <div className="pmc-lg-mcell pmc-empty-cell">— not in merchant data</div>}
              <div className={`pmc-lg-glyph ${cls}`} data-tip={tip}>{g}</div>
              <LearnedAttrRow
                attr={pr.attr}
                entry={dictionary?.[pr.attr.key] ?? null}
                check={check}
                gaps={gaps}
                feedField={pr.attr.origin ? null : feedOriginMatch(product, pr.attr)}
              />
            </React.Fragment>
          );
        })}
      </div>

      {leftovers.length > 0 && (
        <details className="pmc-sdesc">
          <summary data-tip="Merchant fields no learned attribute pairs with — identifiers, URLs, dates, and any spec the enrichment never picked up.">
            <span className="pmc-sk">unused merchant fields</span>
            <span className="pmc-sval pmc-clamp">
              {fmtInt(leftovers.length)} fields enrichment did not pair with
            </span>
          </summary>
          <div className="pmc-leftovers">
            {leftovers.map((f) => (
              <div className="pmc-srow" key={f.key}>
                <div className="pmc-srow-main">
                  <span className="pmc-sk">{f.key}</span>
                  <span className="pmc-sval">{String(f.value ?? '—')}</span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </>
  );
}
